import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import * as mui from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { SideBar } from '../../layout';
import SearchAppBar from '../../common/Appbar';
import Separator from '../../layout/Separator';
import ResponsiveContainer from '../../layout/Container';
import HomeIcon from '@mui/icons-material/Home';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import HRService from '../../../services/HrService';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import DownloadIcon from '@mui/icons-material/Download';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import PersonIcon from '@mui/icons-material/Person';

// Theme for the data grid
const dataGridTheme = createTheme({
  components: {
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: 'none',
          '& .MuiDataGrid-columnHeader': {
            backgroundColor: '#f5f5f5',
            fontSize: '0.875rem',
            fontWeight: 'bold',
          },
          '& .MuiDataGrid-cell': {
            fontSize: '0.875rem',
          },
          '& .MuiDataGrid-row:nth-of-type(odd)': {
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
          },
        },
      },
    },
  },
});

const HRAudit = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [hrRecords, setHrRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [auditStats, setAuditStats] = useState({
    totalRecords: 0,
    completeRecords: 0,
    recordsNeedingAttention: 0,
    activeEmployees: 0,
    inactiveEmployees: 0,
    completionRate: 0,
  });
  const [filterModel, setFilterModel] = useState({
    items: [],
  });
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [recordFilter, setRecordFilter] = useState('all'); // 'all', 'incomplete', 'complete', 'stale'
  const [refreshStats, setRefreshStats] = useState({
    mostRecentRefresh: null,
    staleRecordsCount: 0,
    averageRefreshAge: 0
  });
  const [missingEmailCount, setMissingEmailCount] = useState(0);

  // Check if we need to export the report
  const shouldExport = new URLSearchParams(location.search).get('export') === 'true';
  
  // Export audit report
  const exportAuditReport = useCallback((data) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      console.warn('No data available to export');
      return;
    }
    
    // Count active vs inactive for the filename
    const activeCount = data.filter(r => r?.STATUS?.toLowerCase() === 'active').length;
    const inactiveCount = data.length - activeCount;
    
    // Create CSV content
    const headers = [
      'User ID', 'Email', 'First Name', 'Last Name', 'Job Title', 
      'Department', 'Status', 'Manager', 'Hire Date',
      'Completeness (%)', 'Last Refreshed', 'Modified By'
    ];
    
    const csvRows = [
      headers.join(',')
    ];
    
    data.forEach(record => {
      if (!record) return;
      
      // Format dates for better readability in CSV
      let hireDate = '';
      if (record.HIRE_DATE) {
        try {
          const date = new Date(record.HIRE_DATE);
          if (!isNaN(date.getTime())) {
            hireDate = date.toLocaleDateString();
          } else {
            hireDate = record.HIRE_DATE;
          }
        } catch (e) {
          hireDate = record.HIRE_DATE;
        }
      }
      
      let lastRefreshed = '';
      if (record.LAST_REFRESHED) {
        try {
          const date = new Date(record.LAST_REFRESHED);
          if (!isNaN(date.getTime())) {
            lastRefreshed = date.toLocaleString();
          } else {
            lastRefreshed = record.LAST_REFRESHED;
          }
        } catch (e) {
          lastRefreshed = record.LAST_REFRESHED;
        }
      }
      
      const row = [
        `"${record.USER_ID || ''}"`,
        `"${record.EMAIL_ADDRESS || ''}"`,
        `"${record.FIRST_NAME || ''}"`,
        `"${record.LAST_NAME || ''}"`,
        `"${record.JOB_TITLE || ''}"`,
        `"${record.DEPARTMENT || ''}"`,
        `"${record.STATUS || ''}"`,
        `"${record.MANAGER || ''}"`,
        `"${hireDate || ''}"`,
        record.completeness || 0,
        `"${lastRefreshed || ''}"`,
        `"${record.MODIFIED_BY || ''}"`
      ];
      
      csvRows.push(row.join(','));
    });
    
    const csvContent = csvRows.join('\n');
    
    // Create and download the CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const date = new Date().toISOString().split('T')[0];
    
    // Include active/inactive counts in the filename
    link.setAttribute('href', url);
    link.setAttribute('download', `hr_audit_report_${date}_${data.length}total_${activeCount}active_${inactiveCount}inactive.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Remove export flag from URL
    navigate('/interfaces/hr/audit', { replace: true });
  }, [navigate]);
  
  // Fetch HR records
  useEffect(() => {
    const fetchRecords = async () => {
      try {
        setLoading(true);
        console.log("Fetching HR records for audit (including both active and inactive)...");
        
        // Use the most direct method first - explicitly designed for both active and inactive
        console.log("Using fetchAllHRRecordsIncludingInactive as primary method");
        let records = await HRService.fetchAllHRRecordsIncludingInactive();
        
        // Log detailed information about the records received
        if (records && records.length > 0) {
          const activeCount = records.filter(r => r.STATUS?.toLowerCase() === 'active').length;
          const inactiveCount = records.filter(r => 
            r.STATUS?.toLowerCase() === 'inactive' || 
            r.STATUS?.toLowerCase() === 'terminated'
          ).length;
          const unknownCount = records.length - activeCount - inactiveCount;
          
          console.log(`Received ${records.length} total records:
            - Active: ${activeCount}
            - Inactive/Terminated: ${inactiveCount}
            - Unknown status: ${unknownCount}
          `);
          
          // If we don't have any inactive records, try the reporting method as backup
          if (inactiveCount === 0) {
            console.log("No inactive records found. Trying fetchHRRecordsForReporting as backup...");
            try {
              const reportingRecords = await HRService.fetchHRRecordsForReporting();
              
              // Check if the reporting method gave us more records including inactive ones
              if (reportingRecords && reportingRecords.length > 0) {
                const reportingActiveCount = reportingRecords.filter(r => r.STATUS?.toLowerCase() === 'active').length;
                const reportingInactiveCount = reportingRecords.filter(r => 
                  r.STATUS?.toLowerCase() === 'inactive' || 
                  r.STATUS?.toLowerCase() === 'terminated'
                ).length;
                
                console.log(`Reporting method returned ${reportingRecords.length} records:
                  - Active: ${reportingActiveCount}
                  - Inactive/Terminated: ${reportingInactiveCount}
                `);
                
                // Use reporting records if it contains inactive records or more records overall
                if (reportingInactiveCount > 0 || reportingRecords.length > records.length) {
                  console.log("Using records from reporting method instead as it contains inactive records or more records overall");
                  records = reportingRecords;
                }
              }
            } catch (reportingError) {
              console.error("Error with reporting method:", reportingError);
            }
          }
        }
        
        // Additional check - if we still have few records, it might be due to backend filtering
        // Try to check API parameters and record a diagnostic message
        if (records.length < 10) {
          console.warn("Very few records returned. This could indicate backend filtering for active users only.");
          console.log("URL parameters being used:", {
            include_inactive: true,
            include_all: true,
            limit: 2000,
            for_reporting: true,
            bypass_filters: true
          });
          console.log("If you're still seeing only active users, check your backend API implementation for filters.");
        }
        
        console.log(`Processing ${records?.length || 0} HR records for audit display`);
        
        // Sample logging of date fields
        if (records && records.length > 0) {
          const sampleRecord = records[0];
          console.log("Sample record fields:", {
            USER_ID: sampleRecord.USER_ID,
            EMAIL_ADDRESS: sampleRecord.EMAIL_ADDRESS,
            STATUS: sampleRecord.STATUS,
            HIRE_DATE: sampleRecord.HIRE_DATE,
            HIRE_DATE_TYPE: typeof sampleRecord.HIRE_DATE,
            LAST_REFRESHED: sampleRecord.LAST_REFRESHED,
            LAST_REFRESHED_TYPE: typeof sampleRecord.LAST_REFRESHED,
          });
        }
        
        // Calculate refresh statistics
        let mostRecentRefresh = null;
        let totalRefreshAge = 0;
        let refreshCount = 0;
        const now = new Date();
        
        records.forEach(record => {
          if (record.LAST_REFRESHED) {
            try {
              const refreshDate = new Date(record.LAST_REFRESHED);
              if (!isNaN(refreshDate.getTime())) {
                // Track most recent refresh date
                if (!mostRecentRefresh || refreshDate > new Date(mostRecentRefresh)) {
                  mostRecentRefresh = record.LAST_REFRESHED;
                }
                
                // Calculate age in days for average
                const ageInDays = (now - refreshDate) / (1000 * 60 * 60 * 24);
                totalRefreshAge += ageInDays;
                refreshCount++;
              }
            } catch (e) {
              console.error('Error processing refresh date:', e);
            }
          }
        });
        
        // Calculate average refresh age
        const averageRefreshAge = refreshCount > 0 ? totalRefreshAge / refreshCount : 0;
        
        // Store refresh statistics
        setRefreshStats({
          mostRecentRefresh,
          averageRefreshAge,
          staleRecordsCount: 0 // Will be updated after processing
        });
        
        // Process records for audit purposes
        const processedRecords = (records || []).map((record, index) => {
          if (!record) return null;
          
          const isActive = record.STATUS?.toLowerCase() === 'active';
          
          // Get missing fields
          const requiredFields = [
            'USER_ID', 'EMAIL_ADDRESS', 'FIRST_NAME', 'LAST_NAME', 
            'JOB_TITLE', 'DEPARTMENT', 'STATUS'
          ];
          
          // Adjust optional fields based on employee status
          let optionalFields = [
            'MANAGER', 'HIRE_DATE', 'EMP_TYPE'
          ];
          
          // Only consider TERMINATION_DATE for inactive/terminated employees
          if (!isActive) {
            optionalFields.push('TERMINATION_DATE');
          }
          
          // Get missing required fields
          const missingRequiredFields = requiredFields.filter(field => 
            !record[field] || 
            record[field] === '' || 
            record[field] === null || 
            record[field] === 'NULL'
          );
          
          // Get missing optional fields
          const missingOptionalFields = optionalFields.filter(field => 
            !record[field] || 
            record[field] === '' || 
            record[field] === null || 
            record[field] === 'NULL'
          );
          
          // Calculate completeness
          const completeness = calculateCompleteness(record);
          
          // Create a summary of missing fields
          const missingFieldsSummary = [];
          if (missingRequiredFields.length > 0) {
            missingFieldsSummary.push(`Missing key information: ${missingRequiredFields.join(', ')}`);
          }
          if (missingOptionalFields.length > 0) {
            missingFieldsSummary.push(`Additional information needed: ${missingOptionalFields.join(', ')}`);
          }
          
          // Check if this record is stale
          const refreshDate = record.LAST_REFRESHED ? new Date(record.LAST_REFRESHED) : null;
          let refreshAge = null;
          let isStale = false;
          
          if (refreshDate && !isNaN(refreshDate.getTime())) {
            refreshAge = (now - refreshDate) / (1000 * 60 * 60 * 24);
            
            // A record is stale if:
            // 1. It was refreshed more than 7 days before the most recent refresh
            // 2. OR its refresh age is more than twice the average
            if (mostRecentRefresh) {
              const mostRecentDate = new Date(mostRecentRefresh);
              const daysBehind = (mostRecentDate - refreshDate) / (1000 * 60 * 60 * 24);
              isStale = daysBehind > 7 || (averageRefreshAge > 0 && refreshAge > averageRefreshAge * 2);
            }
          } else {
            // No refresh date is considered stale
            isStale = true;
          }
          
          return {
            ...record,
            id: record.id || `record-${index}`, // Ensure each record has a unique id
            completeness,
            status: record.STATUS || 'Unknown',
            missingRequiredFields,
            missingOptionalFields,
            missingFieldsSummary: missingFieldsSummary.join('\n'),
            isComplete: missingRequiredFields.length === 0,
            needsAttention: missingRequiredFields.length > 0,
            refreshAge,
            isStale
          };
        }).filter(record => record !== null); // Remove any null records
        
        // Count stale records
        const staleRecordsCount = processedRecords.filter(r => r.isStale).length;
        setRefreshStats(prev => ({
          ...prev,
          staleRecordsCount
        }));
        
        console.log(`After processing, ${processedRecords.length} valid HR records remain`);
        console.log(`Refresh stats: Most recent=${mostRecentRefresh}, Average age=${averageRefreshAge.toFixed(1)} days, Stale=${staleRecordsCount}`);
        
        // Filter out records with no EMAIL_ADDRESS
        const countMissingEmail = processedRecords.filter(r => !r.EMAIL_ADDRESS).length;
        setMissingEmailCount(countMissingEmail);
        
        if (countMissingEmail > 0) {
          console.warn(`Found ${countMissingEmail} records with missing email addresses. These will be excluded from the audit view.`);
          const validRecords = processedRecords.filter(r => r.EMAIL_ADDRESS);
          setHrRecords(validRecords);
          console.log(`After filtering out records with missing emails, ${validRecords.length} records remain.`);
        } else {
          setHrRecords(processedRecords);
        }
        
        // Calculate audit statistics
        const totalRecords = processedRecords.length;
        const completeRecords = processedRecords.filter(r => r.completeness === 100).length;
        const recordsNeedingAttention = processedRecords.filter(r => r.missingRequiredFields.length > 0).length;
        const activeEmployees = processedRecords.filter(r => r.STATUS?.toLowerCase() === 'active').length;
        
        console.log(`HR Audit Statistics: 
          Total Records: ${totalRecords}
          Records with All Required Fields: ${completeRecords}
          Records Needing Attention: ${recordsNeedingAttention}
          Active Employees: ${activeEmployees}
        `);
        
        setAuditStats({
          totalRecords,
          completeRecords,
          recordsNeedingAttention,
          activeEmployees,
          inactiveEmployees: totalRecords - activeEmployees,
          completionRate: totalRecords > 0 ? (completeRecords / totalRecords) * 100 : 0,
        });
        
        // Export if required
        if (shouldExport) {
          exportAuditReport(processedRecords);
        }
      } catch (error) {
        console.error('Error fetching HR records for audit:', error);
        setHrRecords([]);
        setAuditStats({
          totalRecords: 0,
          completeRecords: 0,
          recordsNeedingAttention: 0,
          activeEmployees: 0,
          inactiveEmployees: 0,
          completionRate: 0,
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchRecords();
  }, [shouldExport, exportAuditReport]);
  
  // Calculate completeness percentage for a record
  const calculateCompleteness = (record) => {
    if (!record) return 0;
    
    const isActive = record.STATUS?.toLowerCase() === 'active';
    
    const requiredFields = [
      'USER_ID', 'EMAIL_ADDRESS', 'FIRST_NAME', 'LAST_NAME', 
      'JOB_TITLE', 'DEPARTMENT', 'STATUS'
    ];
    
    // Adjust optional fields based on employee status
    let optionalFields = [
      'MANAGER', 'HIRE_DATE', 'EMP_TYPE'
    ];
    
    // Only consider TERMINATION_DATE for inactive/terminated employees
    if (!isActive) {
      optionalFields.push('TERMINATION_DATE');
    }
    
    // Count filled required fields
    const filledRequiredFields = requiredFields.filter(field => 
      record[field] !== null && 
      record[field] !== undefined && 
      record[field] !== '' &&
      record[field] !== 'NULL'
    ).length;
    
    // Count filled optional fields
    const filledOptionalFields = optionalFields.filter(field => 
      record[field] !== null && 
      record[field] !== undefined && 
      record[field] !== '' &&
      record[field] !== 'NULL'
    ).length;
    
    // Calculate weighted completeness
    const requiredWeight = 0.7; // 70% weight for required fields
    const optionalWeight = 0.3; // 30% weight for optional fields
    
    const requiredCompleteness = (filledRequiredFields / requiredFields.length) * requiredWeight * 100;
    const optionalCompleteness = optionalFields.length > 0 
      ? (filledOptionalFields / optionalFields.length) * optionalWeight * 100 
      : optionalWeight * 100; // If no optional fields to consider, give full credit
    
    return Math.round(requiredCompleteness + optionalCompleteness);
  };
  
  // Function to check if a record is stale (not refreshed recently)
  const isStaleRecord = (record) => {
    if (!record.LAST_REFRESHED) return true;
    
    try {
      // If no most recent refresh date is available yet, can't determine staleness
      if (!refreshStats.mostRecentRefresh) return false;
      
      const refreshDate = new Date(record.LAST_REFRESHED);
      const mostRecentDate = new Date(refreshStats.mostRecentRefresh);
      
      // Consider a record stale if it's more than 7 days older than the most recent refresh
      // or if it's more than twice the average refresh age
      const daysDifference = (mostRecentDate - refreshDate) / (1000 * 60 * 60 * 24);
      return daysDifference > 7 || 
             (refreshStats.averageRefreshAge > 0 && daysDifference > refreshStats.averageRefreshAge * 2);
    } catch (e) {
      console.error('Error checking stale record:', e);
      return false;
    }
  };
  
  // Apply record filter
  const filteredByStatusRecords = hrRecords.filter(record => {
    if (!record) return false;
    
    // Apply record completeness filter
    if (recordFilter === 'incomplete') {
      return record.needsAttention === true;
    } else if (recordFilter === 'complete') {
      return record.needsAttention !== true;
    } else if (recordFilter === 'stale') {
      return record.isStale === true;
    }
    
    return true; // 'all' filter
  });
  
  // Filter records based on search query and status filter
  const filteredRecords = filteredByStatusRecords.filter(record => {
    if (!record) return false;
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      (record.USER_ID && record.USER_ID.toLowerCase().includes(query)) ||
      (record.EMAIL_ADDRESS && record.EMAIL_ADDRESS.toLowerCase().includes(query)) ||
      (record.FIRST_NAME && record.FIRST_NAME.toLowerCase().includes(query)) ||
      (record.LAST_NAME && record.LAST_NAME.toLowerCase().includes(query)) ||
      (record.DEPARTMENT && record.DEPARTMENT.toLowerCase().includes(query)) ||
      (record.JOB_TITLE && record.JOB_TITLE.toLowerCase().includes(query))
    );
  });
  
  // Export current filtered data
  const exportFilteredData = () => {
    exportAuditReport(filteredRecords);
  };
  
  // Define columns for the data grid
  const columns = [
    { 
      field: 'USER_ID', 
      headerName: 'User ID', 
      flex: 1, 
      minWidth: 120
    },
    { 
      field: 'EMAIL_ADDRESS', 
      headerName: 'Email', 
      flex: 1.5, 
      minWidth: 180
    },
    { 
      field: 'FIRST_NAME', 
      headerName: 'First Name', 
      flex: 1, 
      minWidth: 120
    },
    { 
      field: 'LAST_NAME', 
      headerName: 'Last Name', 
      flex: 1, 
      minWidth: 120
    },
    { 
      field: 'JOB_TITLE', 
      headerName: 'Job Title', 
      flex: 1.5, 
      minWidth: 180
    },
    { 
      field: 'DEPARTMENT', 
      headerName: 'Department', 
      flex: 1.5, 
      minWidth: 150
    },
    { 
      field: 'STATUS', 
      headerName: 'Status', 
      width: 120,
      renderCell: (params) => {
        if (!params || params.value === undefined || params.value === null) {
          return (
            <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
              <mui.Box sx={{ color: 'warning.main', mr: 1 }}><WarningAmberIcon fontSize="small" /></mui.Box>
              <mui.Typography color="warning.main" fontWeight="medium">
                Unknown
              </mui.Typography>
            </mui.Box>
          );
        }
        
        const status = params.value?.toLowerCase() || '';
        let color, icon;
        
        if (status === 'active') {
          color = 'success.main';
          icon = <PersonIcon fontSize="small" />;
        } else if (status === 'inactive' || status === 'terminated') {
          color = 'error.main';
          icon = <PersonOffIcon fontSize="small" />;
        } else {
          color = 'warning.main';
          icon = <WarningAmberIcon fontSize="small" />;
        }
        
        return (
          <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
            <mui.Box sx={{ color, mr: 1 }}>{icon}</mui.Box>
            <mui.Typography color={color} fontWeight="medium">
              {params.value || 'Unknown'}
            </mui.Typography>
          </mui.Box>
        );
      }
    },
    { 
      field: 'completeness', 
      headerName: 'Completeness Score', 
      width: 160,
      renderCell: (params) => {
        if (!params) {
          return (
            <mui.Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <mui.Box sx={{ width: '100%', mr: 1 }}>
                <mui.LinearProgress
                  variant="determinate"
                  value={0}
                  sx={{ 
                    height: 8, 
                    borderRadius: 5,
                    backgroundColor: 'rgba(0, 0, 0, 0.1)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: 'error.main'
                    }
                  }}
                />
              </mui.Box>
              <mui.Box sx={{ minWidth: 35 }}>
                <mui.Typography variant="body2" color="error.main" fontWeight="bold">
                  0%
                </mui.Typography>
              </mui.Box>
            </mui.Box>
          );
        }
        
        const value = params.value || 0;
        let color;
        
        if (value >= 90) {
          color = 'success.main';
        } else if (value >= 70) {
          color = 'warning.main';
        } else {
          color = 'error.main';
        }
        
        // Create tooltip text based on missing fields
        const missingRequiredCount = params.row?.missingRequiredFields?.length || 0;
        const missingOptionalCount = params.row?.missingOptionalFields?.length || 0;
        const tooltipText = params.row?.missingFieldsSummary || 'No data available';
        
        return (
          <mui.Tooltip 
            title={
              <React.Fragment>
                <mui.Typography color="inherit" variant="body2">Completeness Score: {value}%</mui.Typography>
                {missingRequiredCount > 0 && (
                  <mui.Typography color="error" variant="body2">
                    {missingRequiredCount} key field(s) need to be updated
                  </mui.Typography>
                )}
                {missingOptionalCount > 0 && (
                  <mui.Typography color="warning.main" variant="body2">
                    {missingOptionalCount} additional field(s) could be added
                  </mui.Typography>
                )}
                {tooltipText && (
                  <mui.Typography color="inherit" variant="caption" sx={{ display: 'block', mt: 1 }}>
                    {tooltipText.split('\n').map((line, i) => (
                      <span key={i} style={{ display: 'block' }}>{line}</span>
                    ))}
                  </mui.Typography>
                )}
              </React.Fragment>
            }
            arrow
          >
            <mui.Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <mui.Box sx={{ width: '100%', mr: 1 }}>
                <mui.LinearProgress
                  variant="determinate"
                  value={value}
                  sx={{ 
                    height: 8, 
                    borderRadius: 5,
                    backgroundColor: 'rgba(0, 0, 0, 0.1)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: color
                    }
                  }}
                />
              </mui.Box>
              <mui.Box sx={{ minWidth: 35 }}>
                <mui.Typography variant="body2" color={color} fontWeight="bold">
                  {`${value}%`}
                </mui.Typography>
              </mui.Box>
            </mui.Box>
          </mui.Tooltip>
        );
      }
    },
    { 
      field: 'missingFieldsSummary', 
      headerName: 'Data Completeness', 
      flex: 1.5,
      minWidth: 200,
      renderCell: (params) => {
        if (!params.value) {
          return (
            <mui.Typography variant="body2" color="success.main">
              All fields complete
            </mui.Typography>
          );
        }
        
        const missingRequired = params.row.missingRequiredFields?.length > 0;
        
        return (
          <mui.Box>
            {missingRequired ? (
              <mui.Chip
                icon={<ErrorOutlineIcon />}
                label={`${params.row.missingRequiredFields.length} key fields to update`}
                size="small"
                color="error"
                variant="outlined"
                sx={{ mr: 1 }}
              />
            ) : (
              <mui.Chip
                icon={<CheckCircleOutlineIcon />}
                label="All key fields complete"
                size="small"
                color="success"
                variant="outlined"
                sx={{ mr: 1 }}
              />
            )}
            <mui.Tooltip
              title={
                <React.Fragment>
                  {params.value.split('\n').map((line, i) => (
                    <mui.Typography key={i} variant="body2" component="div">
                      {line}
                    </mui.Typography>
                  ))}
                </React.Fragment>
              }
              arrow
            >
              <InfoOutlinedIcon 
                color="action" 
                fontSize="small"
                sx={{ cursor: 'pointer', verticalAlign: 'middle' }}
              />
            </mui.Tooltip>
          </mui.Box>
        );
      }
    },
    { 
      field: 'LAST_REFRESHED', 
      headerName: 'Last Refreshed', 
      width: 200,
      renderCell: (params) => {
        if (!params.row || !params.row.LAST_REFRESHED) {
          return (
            <mui.Chip
              label="Never Refreshed"
              size="small"
              color="error"
              variant="outlined"
              icon={<ErrorOutlineIcon fontSize="small" />}
            />
          );
        }
        
        try {
          const dateStr = params.row.LAST_REFRESHED;
          const date = new Date(dateStr);
          
          if (!isNaN(date.getTime())) {
            const formattedDate = date.toLocaleString();
            const isStale = params.row.isStale;
            
            if (isStale) {
              return (
                <mui.Tooltip
                  title={
                    <React.Fragment>
                      <mui.Typography variant="subtitle2" component="div">
                        This record has not been refreshed recently
                      </mui.Typography>
                      <mui.Typography variant="body2" component="div">
                        Last refresh: {formattedDate}
                      </mui.Typography>
                      {params.row.refreshAge && (
                        <mui.Typography variant="body2" component="div">
                          {Math.round(params.row.refreshAge)} days since last refresh
                        </mui.Typography>
                      )}
                      <mui.Typography variant="caption" component="div" sx={{ mt: 1 }}>
                        Most recent records were refreshed {Math.round(refreshStats.averageRefreshAge)} days ago on average
                      </mui.Typography>
                    </React.Fragment>
                  }
                  arrow
                >
                  <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <mui.Typography color="warning.main" sx={{ mr: 1 }}>
                      {formattedDate}
                    </mui.Typography>
                    <WarningAmberIcon color="warning" fontSize="small" />
                  </mui.Box>
                </mui.Tooltip>
              );
            }
            
            return formattedDate;
          }
          
          return dateStr;
        } catch (e) {
          return params.row.LAST_REFRESHED || '-';
        }
      }
    },
    { 
      field: 'HIRE_DATE', 
      headerName: 'Hire Date', 
      width: 130,
      renderCell: (params) => {
        if (!params.row || !params.row.HIRE_DATE) return '-';
        try {
          const dateStr = params.row.HIRE_DATE;
          const date = new Date(dateStr);
          if (!isNaN(date.getTime())) {
            return date.toLocaleDateString();
          }
          return dateStr;
        } catch (e) {
          return params.row.HIRE_DATE || '-';
        }
      }
    }
  ];
  
  const customMainContent = (
    <ResponsiveContainer>
      <mui.Breadcrumbs aria-label="breadcrumb">
        <mui.Link underline="hover" color="inherit" href="/Dashboard" sx={{ display: 'flex', alignItems: 'center' }}>
          <HomeIcon sx={{ mr: 0.5, fontSize: '1.1rem' }} />
          Home
        </mui.Link>
        <mui.Link underline="hover" color="inherit" href="/Dashboard">
          Dashboard
        </mui.Link>
        <mui.Link underline="hover" color="inherit" href="/hr-data-management">
          HR Data
        </mui.Link>
        <mui.Typography color="text.primary">HR Audit</mui.Typography>
      </mui.Breadcrumbs>

      <SearchAppBar title="HR Records Audit" icon={<VerifiedUserIcon />} />
      <mui.Typography sx={{ marginTop: '20px' }} variant="subtitle2" gutterBottom>
        Review HR records for completeness and accuracy to support audit requirements. This audit includes both active and inactive/terminated employees.
      </mui.Typography>

      <Separator />
      
      {loading ? (
        <mui.Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <mui.CircularProgress />
        </mui.Box>
      ) : (
        <>
          {/* Audit Stats Cards */}
          <mui.Grid container spacing={2} sx={{ mt: 1, mb: 4 }}>
            <mui.Grid item xs={12} sm={6} md={3} lg={2}>
              <mui.Paper elevation={2} sx={{ p: 2, height: '100%', textAlign: 'center' }}>
                <mui.Typography variant="h4" color="text.primary" sx={{ fontWeight: 'bold' }}>
                  {auditStats.totalRecords}
                </mui.Typography>
                <mui.Typography variant="body2" color="text.secondary">
                  Total Records
                </mui.Typography>
              </mui.Paper>
            </mui.Grid>
            
            <mui.Grid item xs={12} sm={6} md={3} lg={2}>
              <mui.Paper elevation={2} sx={{ p: 2, height: '100%', textAlign: 'center' }}>
                <mui.Typography variant="h4" color="success.main" sx={{ fontWeight: 'bold' }}>
                  {auditStats.completeRecords}
                </mui.Typography>
                <mui.Typography variant="body2" color="text.secondary">
                  Complete Records
                </mui.Typography>
              </mui.Paper>
            </mui.Grid>
            
            <mui.Grid item xs={12} sm={6} md={3} lg={2}>
              <mui.Paper elevation={2} sx={{ p: 2, height: '100%', textAlign: 'center' }}>
                <mui.Typography variant="h4" color="error.main" sx={{ fontWeight: 'bold' }}>
                  {auditStats.recordsNeedingAttention}
                </mui.Typography>
                <mui.Typography variant="body2" color="text.secondary">
                  Records Needing Updates
                </mui.Typography>
              </mui.Paper>
            </mui.Grid>
            
            <mui.Grid item xs={12} sm={6} md={3} lg={2}>
              <mui.Paper elevation={2} 
                sx={{
                  p: 2, 
                  height: '100%', 
                  textAlign: 'center',
                  position: 'relative',
                  ...(refreshStats.staleRecordsCount > 0 && {
                    backgroundColor: 'rgba(255, 152, 0, 0.05)',
                    borderLeft: '4px solid',
                    borderColor: 'warning.main'
                  })
                }}
              >
                <mui.Typography variant="h4" color="warning.main" sx={{ fontWeight: 'bold' }}>
                  {refreshStats.staleRecordsCount}
                </mui.Typography>
                <mui.Typography variant="body2" color="text.secondary">
                  Not Recently Refreshed
                </mui.Typography>
                {refreshStats.staleRecordsCount > 0 && (
                  <mui.Box sx={{ 
                    position: 'absolute', 
                    top: 4, 
                    right: 4,
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <mui.Tooltip title="These records have not been refreshed recently compared to others">
                      <WarningAmberIcon color="warning" fontSize="small" />
                    </mui.Tooltip>
                  </mui.Box>
                )}
              </mui.Paper>
            </mui.Grid>
            
            <mui.Grid item xs={12} sm={6} md={4} lg={2}>
              <mui.Paper elevation={2} sx={{ p: 2, height: '100%', textAlign: 'center' }}>
                <mui.Typography variant="h4" color="primary.main" sx={{ fontWeight: 'bold' }}>
                  {auditStats.completionRate.toFixed(1)}%
                </mui.Typography>
                <mui.Typography variant="body2" color="text.secondary">
                  Overall Completion Rate
                </mui.Typography>
              </mui.Paper>
            </mui.Grid>
            
            <mui.Grid item xs={12} sm={6} md={4} lg={2}>
              <mui.Paper elevation={2} sx={{ p: 2, height: '100%', textAlign: 'center' }}>
                <mui.Typography variant="h4" color="success.main" sx={{ fontWeight: 'bold' }}>
                  {auditStats.activeEmployees}
                </mui.Typography>
                <mui.Typography variant="body2" color="text.secondary">
                  Active Employees
                </mui.Typography>
              </mui.Paper>
            </mui.Grid>
          </mui.Grid>
          
          {/* Filter and Export Controls */}
          <mui.Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <mui.Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <mui.TextField
                label="Search Records"
                variant="outlined"
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ width: 300 }}
                InputProps={{
                  startAdornment: (
                    <mui.InputAdornment position="start">
                      <FilterAltIcon fontSize="small" />
                    </mui.InputAdornment>
                  ),
                }}
              />
              
              <mui.ToggleButtonGroup
                value={recordFilter}
                exclusive
                onChange={(e, newFilter) => {
                  if (newFilter !== null) {
                    setRecordFilter(newFilter);
                  }
                }}
                size="small"
                aria-label="record filter"
              >
                <mui.ToggleButton value="all" aria-label="show all records">
                  All Records
                </mui.ToggleButton>
                <mui.ToggleButton 
                  value="incomplete" 
                  aria-label="show records needing attention"
                  color="error"
                >
                  Needs Attention
                </mui.ToggleButton>
                <mui.ToggleButton 
                  value="complete" 
                  aria-label="show complete records"
                  color="success"
                >
                  Complete Records
                </mui.ToggleButton>
                <mui.ToggleButton 
                  value="stale" 
                  aria-label="show stale records"
                  color="warning"
                >
                  Not Recently Refreshed
                </mui.ToggleButton>
              </mui.ToggleButtonGroup>
            </mui.Box>
            
            <mui.Box>
              <mui.Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={exportFilteredData}
              >
                Export Audit Report (All Statuses)
              </mui.Button>
            </mui.Box>
          </mui.Box>
          
          {/* Record count summary */}
          <mui.Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <mui.Typography variant="body2" color="text.secondary">
              Showing {filteredRecords.length} of {hrRecords.length} records
              {recordFilter !== 'all' && (
                <mui.Chip 
                  label={`Filter: ${recordFilter === 'incomplete' ? 'Needs Attention' : recordFilter === 'complete' ? 'Complete Records' : 'Stale Records'}`} 
                  size="small" 
                  color={recordFilter === 'incomplete' ? 'error' : recordFilter === 'complete' ? 'success' : 'warning'}
                  onDelete={() => setRecordFilter('all')}
                  sx={{ ml: 1 }}
                />
              )}
            </mui.Typography>
            
            {recordFilter === 'incomplete' && (
              <mui.Typography variant="body2" color="error">
                <ErrorOutlineIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                {filteredRecords.length} records need key information updated
              </mui.Typography>
            )}

            {recordFilter === 'stale' && (
              <mui.Typography variant="body2" color="warning.main">
                <WarningAmberIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                {filteredRecords.length} records have not been refreshed recently compared to others
              </mui.Typography>
            )}
          </mui.Box>
          
          {/* Show alert when we have fewer records than expected */}
            {auditStats.totalRecords < 10 && (
              <mui.Alert 
                severity="info" 
                sx={{ mb: 2 }}
                action={
                  <mui.Button 
                    color="inherit" 
                    size="small"
                    onClick={() => window.location.reload()}
                  >
                    Reload
                  </mui.Button>
                }
              >
                <mui.AlertTitle>Limited Records Displayed</mui.AlertTitle>
                Only {auditStats.totalRecords} HR records were loaded. If you expect to see more records:
                <ul style={{ marginTop: 5, marginBottom: 5 }}>
                  <li>The backend API might be applying an <strong>active-only filter</strong> despite the include_inactive=true parameter</li>
                  <li>Check your API implementation for any filters applied in the view or data access layer</li>
                  <li>Verify that inactive users exist in your database</li>
                  <li>Look for server-side logs to see what SQL/query is being executed</li>
                  <li>Records with missing email addresses are being excluded from the audit view</li>
                </ul>
                The frontend is requesting both active and inactive records with parameters: include_inactive=true, include_all=true.
                Try reloading the page or contact your administrator if the issue persists.
              </mui.Alert>
            )}
            
            {/* Show alert about missing emails if present */}
            {missingEmailCount > 0 && (
              <mui.Alert 
                severity="warning" 
                sx={{ mb: 2 }}
              >
                <mui.AlertTitle>Records with Missing Emails Excluded</mui.AlertTitle>
                <p>
                  {missingEmailCount} records were excluded from the audit view because they were missing email addresses.
                  Email addresses are required for proper identification of employees.
                </p>
                <p>
                  You may want to check your HR data source to ensure all employee records have valid email addresses.
                </p>
              </mui.Alert>
            )}
            
            {/* Show success alert when we have both active and inactive users */}
            {auditStats.inactiveEmployees > 0 && (
              <mui.Alert 
                severity="success" 
                sx={{ mb: 2 }}
              >
                <mui.AlertTitle>Complete HR Records Dataset</mui.AlertTitle>
                Successfully loaded both active ({auditStats.activeEmployees}) and inactive ({auditStats.inactiveEmployees}) employee records.
                This provides a complete dataset for audit purposes.
              </mui.Alert>
            )}
          
          {/* Data Grid with HR Records */}
          <ThemeProvider theme={dataGridTheme}>
            <mui.Paper elevation={3} sx={{ height: 650, width: '100%', boxShadow: 2 }}>
              <DataGrid
                rows={filteredRecords || []}
                columns={columns || []}
                pagination
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                filterMode="server"
                filterModel={filterModel}
                onFilterModelChange={setFilterModel}
                pageSizeOptions={[10, 25, 50, 100]}
                disableRowSelectionOnClick
                density="standard"
                getRowClassName={(params) => {
                  if (!params || !params.row) return '';
                  
                  if (params.row.needsAttention && params.row.isStale) {
                    return 'needs-attention-and-stale-row';
                  } else if (params.row.needsAttention) {
                    return 'needs-attention-row';
                  } else if (params.row.isStale) {
                    return 'stale-row';
                  }
                  
                  return '';
                }}
                sx={{
                  '& .needs-attention-row': {
                    bgcolor: 'rgba(255, 0, 0, 0.03)',
                  },
                  '& .stale-row': {
                    bgcolor: 'rgba(255, 152, 0, 0.03)',
                  },
                  '& .needs-attention-and-stale-row': {
                    bgcolor: 'rgba(255, 72, 0, 0.05)',
                  },
                }}
                slots={{
                  toolbar: GridToolbar,
                  noRowsOverlay: () => (
                    <mui.Stack height="100%" alignItems="center" justifyContent="center">
                      <mui.Typography color="text.secondary">
                        No HR records found
                      </mui.Typography>
                    </mui.Stack>
                  ),
                }}
                slotProps={{
                  toolbar: {
                    showQuickFilter: true,
                    quickFilterProps: { debounceMs: 500 },
                  },
                }}
              />
            </mui.Paper>
          </ThemeProvider>
          
          {/* Completeness Legend */}
          <mui.Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
            <mui.Typography variant="subtitle1" gutterBottom fontWeight="medium">
              Data Completeness Guidelines for HR Records
            </mui.Typography>
            
            <mui.Grid container spacing={2}>
              <mui.Grid item xs={12} md={4}>
                <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <mui.Box sx={{ 
                    width: 12, 
                    height: 12, 
                    borderRadius: '50%', 
                    bgcolor: 'success.main',
                    mr: 1 
                  }} />
                  <mui.Typography variant="body2">
                    <strong>90-100%:</strong> Complete record with all essential data
                  </mui.Typography>
                </mui.Box>
              </mui.Grid>
              
              <mui.Grid item xs={12} md={4}>
                <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <mui.Box sx={{ 
                    width: 12, 
                    height: 12, 
                    borderRadius: '50%', 
                    bgcolor: 'warning.main',
                    mr: 1 
                  }} />
                  <mui.Typography variant="body2">
                    <strong>70-89%:</strong> Record may need additional details
                  </mui.Typography>
                </mui.Box>
              </mui.Grid>
              
              <mui.Grid item xs={12} md={4}>
                <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <mui.Box sx={{ 
                    width: 12, 
                    height: 12, 
                    borderRadius: '50%', 
                    bgcolor: 'error.main',
                    mr: 1 
                  }} />
                  <mui.Typography variant="body2">
                    <strong>Below 70%:</strong> Record needs key information updated
                  </mui.Typography>
                </mui.Box>
              </mui.Grid>
            </mui.Grid>
            
            <mui.Divider sx={{ my: 2 }} />
            
            <mui.Typography variant="subtitle2" gutterBottom>
              Field Requirements:
            </mui.Typography>
            
            <mui.Grid container spacing={2}>
              <mui.Grid item xs={12} md={6}>
                <mui.Paper variant="outlined" sx={{ p: 2 }}>
                  <mui.Typography variant="body2" fontWeight="bold" color="error.main" gutterBottom>
                    Key Information (70% of score)
                  </mui.Typography>
                  <mui.List dense disablePadding>
                    <mui.ListItem disableGutters>
                      <mui.ListItemIcon sx={{ minWidth: 24 }}>
                        <mui.Box component="span" sx={{ color: 'error.main', fontSize: '1.2rem' }}>•</mui.Box>
                      </mui.ListItemIcon>
                      <mui.ListItemText primary="User ID" />
                    </mui.ListItem>
                    <mui.ListItem disableGutters>
                      <mui.ListItemIcon sx={{ minWidth: 24 }}>
                        <mui.Box component="span" sx={{ color: 'error.main', fontSize: '1.2rem' }}>•</mui.Box>
                      </mui.ListItemIcon>
                      <mui.ListItemText primary="Email Address" />
                    </mui.ListItem>
                    <mui.ListItem disableGutters>
                      <mui.ListItemIcon sx={{ minWidth: 24 }}>
                        <mui.Box component="span" sx={{ color: 'error.main', fontSize: '1.2rem' }}>•</mui.Box>
                      </mui.ListItemIcon>
                      <mui.ListItemText primary="First Name" />
                    </mui.ListItem>
                    <mui.ListItem disableGutters>
                      <mui.ListItemIcon sx={{ minWidth: 24 }}>
                        <mui.Box component="span" sx={{ color: 'error.main', fontSize: '1.2rem' }}>•</mui.Box>
                      </mui.ListItemIcon>
                      <mui.ListItemText primary="Last Name" />
                    </mui.ListItem>
                    <mui.ListItem disableGutters>
                      <mui.ListItemIcon sx={{ minWidth: 24 }}>
                        <mui.Box component="span" sx={{ color: 'error.main', fontSize: '1.2rem' }}>•</mui.Box>
                      </mui.ListItemIcon>
                      <mui.ListItemText primary="Job Title" />
                    </mui.ListItem>
                    <mui.ListItem disableGutters>
                      <mui.ListItemIcon sx={{ minWidth: 24 }}>
                        <mui.Box component="span" sx={{ color: 'error.main', fontSize: '1.2rem' }}>•</mui.Box>
                      </mui.ListItemIcon>
                      <mui.ListItemText primary="Department" />
                    </mui.ListItem>
                    <mui.ListItem disableGutters>
                      <mui.ListItemIcon sx={{ minWidth: 24 }}>
                        <mui.Box component="span" sx={{ color: 'error.main', fontSize: '1.2rem' }}>•</mui.Box>
                      </mui.ListItemIcon>
                      <mui.ListItemText primary="Status" />
                    </mui.ListItem>
                  </mui.List>
                </mui.Paper>
              </mui.Grid>
              
              <mui.Grid item xs={12} md={6}>
                <mui.Paper variant="outlined" sx={{ p: 2 }}>
                  <mui.Typography variant="body2" fontWeight="bold" color="info.main" gutterBottom>
                    Additional Details (30% of score)
                  </mui.Typography>
                  <mui.List dense disablePadding>
                    <mui.ListItem disableGutters>
                      <mui.ListItemIcon sx={{ minWidth: 24 }}>
                        <mui.Box component="span" sx={{ color: 'info.main', fontSize: '1.2rem' }}>•</mui.Box>
                      </mui.ListItemIcon>
                      <mui.ListItemText primary="Manager" />
                    </mui.ListItem>
                    <mui.ListItem disableGutters>
                      <mui.ListItemIcon sx={{ minWidth: 24 }}>
                        <mui.Box component="span" sx={{ color: 'info.main', fontSize: '1.2rem' }}>•</mui.Box>
                      </mui.ListItemIcon>
                      <mui.ListItemText primary="Hire Date" />
                    </mui.ListItem>
                    <mui.ListItem disableGutters>
                      <mui.ListItemIcon sx={{ minWidth: 24 }}>
                        <mui.Box component="span" sx={{ color: 'info.main', fontSize: '1.2rem' }}>•</mui.Box>
                      </mui.ListItemIcon>
                      <mui.ListItemText primary="Employee Type" />
                    </mui.ListItem>
                    <mui.ListItem disableGutters>
                      <mui.ListItemIcon sx={{ minWidth: 24 }}>
                        <mui.Box component="span" sx={{ color: 'info.main', fontSize: '1.2rem' }}>•</mui.Box>
                      </mui.ListItemIcon>
                      <mui.ListItemText primary={<>Termination Date <mui.Typography variant="caption" color="text.secondary">(Only for non-active employees)</mui.Typography></>} />
                    </mui.ListItem>
                  </mui.List>
                </mui.Paper>
              </mui.Grid>
            </mui.Grid>
            
            <mui.Box sx={{ mt: 2, p: 1, bgcolor: 'info.light', borderRadius: 1, display: 'flex', alignItems: 'center' }}>
              <InfoOutlinedIcon sx={{ color: 'info.dark', mr: 1 }} />
              <mui.Typography variant="body2" sx={{ color: 'info.dark' }}>
                This report helps identify specific HR data that may need to be updated for audit purposes. Records flagged as "Needs Attention" are missing key information, while those marked as "Not Recently Refreshed" may contain outdated information compared to the rest of the population.
              </mui.Typography>
            </mui.Box>
            
            {refreshStats.staleRecordsCount > 0 && (
              <mui.Box sx={{ mt: 2, p: 1, bgcolor: 'warning.light', borderRadius: 1, display: 'flex', alignItems: 'center' }}>
                <WarningAmberIcon sx={{ color: 'warning.dark', mr: 1 }} />
                <mui.Typography variant="body2" sx={{ color: 'warning.dark' }}>
                  <strong>{refreshStats.staleRecordsCount} records</strong> have not been refreshed recently compared to others. 
                  The average record was last refreshed {Math.round(refreshStats.averageRefreshAge)} days ago.
                  Records are considered stale if they haven't been refreshed in more than 7 days after the most recent refresh date 
                  or if they are significantly older than the average.
                </mui.Typography>
              </mui.Box>
            )}
          </mui.Box>
        </>
      )}
    </ResponsiveContainer>
  );

  return (
    <div>
      <SideBar mainContent={customMainContent} />
    </div>
  );
};

export default HRAudit; 